import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { OrgLayout } from "../../../components/Shell";
import { ErrorState, Skeleton } from "../../../components/States";
import { ConfirmDialog, Field, PageHeader, Switch, useToast } from "../../../components/ui";
import { SaveBar, settingsEqual } from "../../../components/UnsavedChanges";
import type { AiRoutingMode } from "@backstory/protocol";
import { useCan } from "../../../auth/roles";
import { api, queryKeys, request } from "../../../api";
import { orgAiAdmin } from "../../ai-admin/api";
import { aiApi, aiKeys, savedGitHubRepositories, type AiSettingsExt, type AiSettingsPatch } from "../api";
import "../ai.css";

const enc = encodeURIComponent;
function sameRepos(left: string[], right: string[]) {
  if (left.length !== right.length) return false;
  const a = [...left].sort();
  const b = [...right].sort();
  return a.every((item, index) => item === b[index]);
}

const editableAiSettings = (value: AiSettingsExt) => ({
  enabled: value.enabled,
  perUserDailyLimit: value.perUserDailyLimit,
  deepAnalysisQuality: value.deepAnalysisQuality,
  digest: value.digest,
});

export function AiSettingsPage() {
  const { org } = useParams({ strict: false }) as { org: string };
  const can = useCan();
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: aiKeys.settings(org), queryFn: () => aiApi.settings(org) as Promise<AiSettingsExt>, retry: false });
  const orgRow = useQuery({ queryKey: queryKeys.org(org), queryFn: () => api.orgs.get(org), retry: false });
  const digestCredits = q.data?.digestCredits;
  const digestAllowed = (orgRow.data != null && orgRow.data.plan !== "free") || (digestCredits?.remaining ?? 0) > 0;
  const digestHint = !digestAllowed
    ? "Included on Team and above."
    : digestCredits && digestCredits.remaining > 0 && orgRow.data?.plan === "free"
      ? `${digestCredits.remaining === 1 ? "1 digest left" : `${digestCredits.remaining} digests left`}${digestCredits.expiresAt ? `, through ${new Date(digestCredits.expiresAt).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}` : ""}.`
      : "A rollup of what the AI noticed, delivered to the channels you pick.";
  const channels = useQuery({ queryKey: ["notifications", "channels", org], queryFn: () => request<Array<{ id: string; name: string; type: string }>>(`/v1/notifications/channels?org=${enc(org)}`), retry: false });
  const [draft, setDraft] = useState<AiSettingsExt | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  useEffect(() => {
    if (q.data && !draft) {
      setDraft(q.data);
      setSelected(savedGitHubRepositories(q.data));
    }
  }, [q.data, draft]);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const result = params.get("github");
    if (!result) return;
    if (result === "connected") toast.push("success", "GitHub connected. Choose the repositories Backstory can use.");
    else toast.push("error", "GitHub could not be connected. Try again.");
    params.delete("github");
    const rest = params.toString();
    window.history.replaceState(null, "", window.location.pathname + (rest ? `?${rest}` : ""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applySaved = (s: AiSettingsExt) => {
    setDraft(s);
    setSelected(savedGitHubRepositories(s));
    qc.setQueryData(aiKeys.settings(org), s);
    void qc.invalidateQueries({ queryKey: aiKeys.settings(org) });
  };
  const save = useMutation({
    mutationFn: (patch: AiSettingsPatch) => aiApi.patchSettings(org, patch),
    onSuccess: (s) => {
      toast.push("success", "AI settings saved");
      applySaved(s as AiSettingsExt);
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const disconnect = useMutation({
    mutationFn: () => aiApi.patchSettings(org, { github: { clear: true } }),
    onSuccess: (s) => {
      toast.push("success", "GitHub disconnected");
      setDisconnectOpen(false);
      applySaved(s as AiSettingsExt);
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const savedRepos = savedGitHubRepositories(q.data);
  const githubDirty = !sameRepos(selected, savedRepos);
  const dirty = (!!draft && !!q.data && !settingsEqual(editableAiSettings(draft), editableAiSettings(q.data))) || githubDirty;

  if (q.isPending || !draft) return <OrgLayout orgId={org}><Skeleton lines={8} /></OrgLayout>;
  if (q.isError) return <OrgLayout orgId={org}><ErrorState error={q.error} retry={() => void q.refetch()} /></OrgLayout>;
  const d = draft;
  const set = (patch: Partial<AiSettingsExt>) => setDraft({ ...d, ...patch });

  const onSave = () => {
    const patch: AiSettingsPatch = {
      enabled: d.enabled,
      perUserDailyLimit: d.perUserDailyLimit,
      deepAnalysisQuality: d.deepAnalysisQuality,
      digest: d.digest,
    };
    if (githubDirty) patch.github = { repositories: selected };
    save.mutate(patch);
  };
  const github = q.data?.integrations?.github;
  const available = github?.available ?? null;
  const choices = available ?? selected;
  const discard = () => {
    if (!q.data) return;
    setDraft(q.data);
    setSelected(savedGitHubRepositories(q.data));
  };
  const toggleRepo = (name: string) => {
    setSelected((current) => current.includes(name) ? current.filter((item) => item !== name) : [...current, name]);
  };

  return (
    <OrgLayout orgId={org}>
      <PageHeader title="AI settings" />
      <div className="card form-card">
        <section className="section">
          <h2>AI features</h2>
          <div className="settings-stack">
            <Switch
              checked={d.enabled}
              onChange={(v) => set({ enabled: v })}
              label="AI enabled"
              hint="Turns every AI feature off for this organization, including summaries, natural-language search, and digests."
            />
            <div className="field-row">
              <Field label="Per-user daily limit" hint="AI requests per member per day. Leave blank for no limit.">
                {(p) => (
                  <input
                    {...p}
                    className="input"
                    type="number"
                    min={0}
                    value={d.perUserDailyLimit ?? ""}
                    onChange={(e) => set({ perUserDailyLimit: e.target.value ? Number(e.target.value) : undefined })}
                  />
                )}
              </Field>
              <Field label="Analysis quality" hint="Best spends more credits on each deep analysis than Balanced.">
                {(p) => (
                  <select {...p} className="input" value={d.deepAnalysisQuality} onChange={(e) => set({ deepAnalysisQuality: e.target.value as "best" | "balanced" })}>
                    <option value="best">Best quality</option>
                    <option value="balanced">Balanced</option>
                  </select>
                )}
              </Field>
            </div>
          </div>
        </section>

        <section className="section">
          <h2>Digests</h2>
          <div className="settings-stack">
            <Switch
              checked={digestAllowed && d.digest.enabled}
              disabled={!digestAllowed}
              onChange={(v) => set({ digest: { ...d.digest, enabled: v } })}
              label="Send a recurring digest"
              hint={digestHint}
            />
            <Field label="Digest period">
              {(p) => (
                <select {...p} className="input" style={{ maxWidth: 220 }} value={d.digest.period} onChange={(e) => set({ digest: { ...d.digest, period: e.target.value as "daily" | "weekly" } })}>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                </select>
              )}
            </Field>
            {digestAllowed && d.digest.enabled && (
              <Field label="Digest channels" hint="Where the digest is delivered.">
                {() => (
                  <div className="pill-row">
                    {(channels.data ?? []).map((c) => {
                      const on = d.digest.channelIds.includes(c.id);
                      return (
                        <button key={c.id} type="button" className="btn" aria-pressed={on} onClick={() => set({ digest: { ...d.digest, channelIds: on ? d.digest.channelIds.filter((x) => x !== c.id) : [...d.digest.channelIds, c.id] } })}>
                          {c.type} · {c.name}
                        </button>
                      );
                    })}
                    {channels.isSuccess && channels.data.length === 0 && <span className="small muted">No channels yet. Add one under Notifications → Channels.</span>}
                    {channels.isError && <span className="small muted">Channels unavailable.</span>}
                  </div>
                )}
              </Field>
            )}
          </div>
        </section>

        <AiSourceSection org={org} settings={d} />

        <section className="section">
          <h2>GitHub</h2>
          <div className="settings-stack">
            <p className="muted" style={{ margin: 0 }}>
              Authorize Backstory on GitHub, then choose every repository a draft pull request can open in.
              {github?.connected ? <> <span className="badge ok">Connected</span></> : <> <span className="badge neutral">Not connected</span></>}
            </p>
            {github?.connected && available == null && <p className="muted" style={{ margin: 0 }}>GitHub&apos;s repository list could not be loaded.</p>}
            {github?.connected && available != null && available.length === 0 && <p className="muted" style={{ margin: 0 }}>GitHub did not share any repositories yet. Add some, then return here.</p>}
            {github?.connected && choices.length > 0 && (
              <div role="group" aria-label="Repositories" style={{ display: "grid", gap: 6 }}>
                {choices.map((name) => (
                  <label key={name} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <input type="checkbox" checked={selected.includes(name)} disabled={!can.admin} autoComplete="off" onChange={() => toggleRepo(name)} />
                    <span className="mono">{name}</span>
                  </label>
                ))}
              </div>
            )}
            <div className="actions-row">
              {can.admin && github?.connectUrl && <a className="btn" href={github.connectUrl}>{github.connected ? "Add or remove repositories" : "Connect GitHub"}</a>}
              {can.admin && github?.connected && (
                <button type="button" className="btn" onClick={() => setDisconnectOpen(true)} disabled={disconnect.isPending}>Disconnect</button>
              )}
            </div>
          </div>
        </section>
      </div>
      <SaveBar
        dirty={dirty}
        saving={save.isPending}
        onSave={onSave}
        onDiscard={discard}
        testId="ai-save"
      />
      <ConfirmDialog
        open={disconnectOpen}
        danger
        title="Disconnect GitHub?"
        confirmLabel={disconnect.isPending ? "Disconnecting…" : "Disconnect GitHub"}
        busy={disconnect.isPending}
        onConfirm={() => disconnect.mutate()}
        onClose={() => setDisconnectOpen(false)}
      >
        <p>Draft pull requests stop until GitHub is connected again.</p>
      </ConfirmDialog>
    </OrgLayout>
  );
}

/**
 * Where this organization's AI comes from. The two sources are mutually
 * exclusive by design: an organization that has gone to the trouble of naming
 * its own connections does not want the platform silently answering and
 * charging for calls its own providers refused. Switching is a separate request
 * from the rest of this form because the server can refuse it — organization
 * mode is only allowed once every required route exists — and a saved routing
 * change decides who pays for every subsequent call.
 */
function AiSourceSection({ org, settings }: { org: string; settings: AiSettingsExt }) {
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const admin = useMemo(() => orgAiAdmin(org), [org]);
  // A server that predates organization routing reports nothing, and platform
  // defaults are then the only truthful answer.
  const mode: AiRoutingMode = settings.routingMode ?? "platform";
  // How far the org's own routing has got is computed where the gateway is
  // reachable, so it is read from the admin config rather than duplicated onto
  // settings where the two answers could disagree. A member who may not
  // administer AI simply sees the current source without the switch.
  const config = useQuery({ queryKey: admin.keys.config, queryFn: admin.config, retry: false });
  const routing = config.data?.organizationRouting;
  const switchMode = useMutation({
    mutationFn: (next: AiRoutingMode) => admin.setRoutingMode(next),
    onSuccess: (next) => {
      toast.push("success", next.active ? "Now using your own providers" : "Now using Backstory platform defaults");
      void qc.invalidateQueries({ queryKey: aiKeys.settings(org) });
      void qc.invalidateQueries({ queryKey: aiKeys.capability() });
      void qc.invalidateQueries({ queryKey: admin.keys.config });
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const ready = routing?.ready ?? false;
  const mayAdminister = config.isSuccess;
  const chooseOwn = () => {
    if (ready) {
      switchMode.mutate("organization");
      return;
    }
    void navigate({
      to: (routing?.connectionCount ?? 0) > 0 ? "/orgs/$org/ai/routing" : "/orgs/$org/ai/providers",
      params: { org },
    });
  };

  return (
    <section className="section">
      <h2>AI source</h2>
      <div className="settings-stack">
        <div className="ai-source-choices" role="radiogroup" aria-label="AI source">
          <label className="ai-source-choice">
            <input
              type="radio"
              name="ai-routing-mode"
              value="platform"
              autoComplete="off"
              checked={mode === "platform"}
              disabled={switchMode.isPending || !mayAdminister}
              onChange={() => switchMode.mutate("platform")}
            />
            <span>
              <strong>Backstory platform defaults</strong>
              <span className="muted small">Models chosen and kept current by Backstory. Usage draws on your plan's AI credits.</span>
            </span>
          </label>
          <label className="ai-source-choice">
            <input
              type="radio"
              name="ai-routing-mode"
              value="organization"
              autoComplete="off"
              checked={mode === "organization"}
              disabled={switchMode.isPending || !mayAdminister}
              onChange={chooseOwn}
            />
            <span>
              <strong>Your own providers and routing</strong>
              <span className="muted small">
                Only your connections answer, in the fallback order you set. Your vendors bill you directly and these calls use no AI credits. If your routing
                cannot answer, the request fails rather than falling back to Backstory.
              </span>
            </span>
          </label>
        </div>
        {mayAdminister && mode === "organization" && !ready && (
          <p className="muted small" style={{ marginTop: 6 }}>
            Your own providers are not ready to answer yet.{" "}
            <Link to="/orgs/$org/ai/providers" params={{ org }}>Add providers</Link>
          </p>
        )}
        {routing?.complianceWarning && (
          <p className="small ai-validation" role="note" style={{ marginTop: 6 }}>
            One or more of your PHI routes uses a provider outside Backstory's business associate agreements. Backstory makes no compliance claim about those
            calls; the agreement with that provider is yours.
          </p>
        )}
      </div>
    </section>
  );
}
